import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["ticketType", "ticketPrice", "confirmButton", "toastMessage"]

    connect() {
        this.selectedTicketType = null;
        this.selectedTicketLabel = null;
        this.selectedTicketPrice = null;
        this.selectedPaymentMethod = null;
        this.selectedQuantity = 1;

        // Observer pour surveiller les nouveaux messages flash
        this.setupFlashMessageObserver();
    }

    // Configurer l'observer pour les messages flash
    setupFlashMessageObserver() {
        const flashContainer = document.getElementById('flash-messages');
        if (flashContainer) {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains('alert')) {
                            this.autoHideFlashMessage(node);
                        }
                    });
                });
            });

            observer.observe(flashContainer, { childList: true });
            this.flashObserver = observer;
        }
    }

    // Faire disparaître automatiquement un message flash après 3 secondes
    autoHideFlashMessage(flashElement) {
        setTimeout(() => {
            if (flashElement && flashElement.parentNode) {
                // Ajouter une animation de fade out
                flashElement.style.transition = 'opacity 0.5s ease-out';
                flashElement.style.opacity = '0';

                // Supprimer l'élément après l'animation
                setTimeout(() => {
                    if (flashElement && flashElement.parentNode) {
                        flashElement.remove();
                    }
                }, 500);
            }
        }, 3000); // 3 secondes
    }

    disconnect() {
        // Nettoyer l'observer quand le controller est déconnecté
        if (this.flashObserver) {
            this.flashObserver.disconnect();
        }
    }

    // Action appelée quand on clique sur un bouton de ticket
    selectTicket(event) {
        this.selectedTicketType = event.currentTarget.dataset.ticketType;
        this.selectedTicketLabel = event.currentTarget.dataset.ticketLabel;
        this.selectedTicketPrice = event.currentTarget.dataset.ticketPrice;
        this.selectedPaymentMethod = null; // Reset payment method selection

        // Si c'est un ticket gratuit, utiliser la modale simplifiée
        if (this.selectedTicketType === 'gratuit') {
            this.showFreeTicketModal();
        } else {
            this.showPaidTicketModal();
        }
    }

    // Afficher la modale pour les tickets gratuits
    showFreeTicketModal() {
        // Réinitialiser la quantité
        this.selectedQuantity = 1;
        document.getElementById('freeQuantityInput').value = 1;
        this.updateFreeQuantityDisplay();

        // Mettre à jour le contenu de la modale gratuite
        document.getElementById('freeTicketTypeText').textContent = this.selectedTicketLabel;

        // Définir automatiquement le mode de paiement à "free"
        this.selectedPaymentMethod = 'free';

        // Afficher la modale avec Bootstrap
        const modalElement = document.getElementById('confirmFreeTicketModal');
        const modal = new window.bootstrap.Modal(modalElement);
        this.currentModal = modal;
        modal.show();
    }

    // Afficher la modale pour les tickets payants
    showPaidTicketModal() {
        // Réinitialiser la quantité
        this.selectedQuantity = 1;
        document.getElementById('quantityInput').value = 1;
        this.updateQuantityDisplay();

        // Mettre à jour le contenu de la modale payante
        document.getElementById('ticketTypeText').textContent = this.selectedTicketLabel;
        document.getElementById('ticketPriceText').textContent = this.selectedTicketPrice;

        // Réinitialiser l'état des boutons de paiement
        this.resetPaymentMethodSelection();

        // Afficher la modale avec Bootstrap
        const modalElement = document.getElementById('confirmTicketModal');
        const modal = new window.bootstrap.Modal(modalElement);
        this.currentModal = modal;
        modal.show();
    }

    // Action appelée quand on sélectionne un mode de paiement
    selectPaymentMethod(event) {
        const button = event.currentTarget;
        this.selectedPaymentMethod = button.dataset.paymentMethod;

        // Retirer la sélection de tous les boutons
        document.querySelectorAll('.payment-method-btn').forEach(btn => {
            btn.classList.remove('btn-success', 'btn-primary');
            btn.classList.add('btn-outline-success', 'btn-outline-primary');
        });

        // Marquer le bouton sélectionné
        if (this.selectedPaymentMethod === 'cash') {
            button.classList.remove('btn-outline-success');
            button.classList.add('btn-success');
        } else {
            button.classList.remove('btn-outline-primary');
            button.classList.add('btn-primary');
        }

        // Afficher l'info de sélection
        const paymentInfo = document.getElementById('paymentSelectedInfo');
        const paymentText = document.getElementById('selectedPaymentText');
        const paymentLabel = this.selectedPaymentMethod === 'cash' ? 'Espèces' : 'Carte';

        paymentText.textContent = paymentLabel;
        paymentInfo.classList.remove('d-none');

        // Activer le bouton de confirmation
        const confirmBtn = document.getElementById('confirmCreateTicket');
        confirmBtn.disabled = false;
    }

    // Action appelée pour confirmer la création du ticket
    confirmCreate(event) {
        if (!this.selectedTicketType) {
            return;
        }

        if (!this.selectedPaymentMethod) {
            this.showErrorMessage('Veuillez sélectionner un mode de paiement');
            return;
        }

        const button = event.currentTarget;

        // Désactiver le bouton pendant la requête
        button.disabled = true;
        button.textContent = 'Création en cours...';

        // Obtenir le token CSRF
        const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

        // Utiliser fetch au lieu de créer un formulaire pour une meilleure gestion d'erreurs
        const formData = new FormData();
        formData.append('authenticity_token', token);
        formData.append('ticket_type', this.selectedTicketType);
        formData.append('payment_method', this.selectedPaymentMethod);
        formData.append('quantity', this.selectedQuantity);

        const headers = {
            'Accept': 'text/vnd.turbo-stream.html',
            'X-CSRF-Token': token
        };

        fetch('/tickets', {
            method: 'POST',
            body: formData,
            headers: headers,
            credentials: 'same-origin'
        })
            .then(response => {
                if (response.ok) {
                    return response.text();
                } else {
                    console.error('Request failed with status:', response.status);
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
            })
            .then(turboStreamContent => {
                // Appliquer le contenu Turbo Stream manuellement avec gestion des Trusted Types
                if (turboStreamContent && typeof Turbo !== 'undefined') {
                    try {
                        Turbo.renderStreamMessage(turboStreamContent);
                    } catch (error) {
                        console.warn('Turbo renderStreamMessage failed:', error);
                        // Fallback: parser et appliquer manuellement
                        this.applyTurboStreamFallback(turboStreamContent);
                    }
                }

                // Cacher la modale
                if (this.currentModal) {
                    this.currentModal.hide();
                }

                // Réinitialiser
                this.resetSelection();
            })
            .catch(error => {
                console.error('Error creating ticket:', error);

                // Afficher un message d'erreur
                this.showErrorMessage(`Erreur lors de la création du ticket: ${error.message}`);
            })
            .finally(() => {
                // Réactiver le bouton
                button.disabled = false;
                button.textContent = 'Oui, créer le ticket';
            });


    }

    // Action appelée pour confirmer la création d'un ticket gratuit
    confirmCreateFree(event) {
        if (!this.selectedTicketType) {
            return;
        }

        const button = event.currentTarget;

        // Désactiver le bouton pendant la requête
        button.disabled = true;
        button.textContent = 'Création en cours...';

        // Obtenir le token CSRF
        const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

        // Utiliser fetch pour créer le ticket gratuit
        const formData = new FormData();
        formData.append('authenticity_token', token);
        formData.append('ticket_type', this.selectedTicketType);
        formData.append('payment_method', 'free');
        formData.append('quantity', this.selectedQuantity);

        const headers = {
            'Accept': 'text/vnd.turbo-stream.html',
            'X-CSRF-Token': token
        };

        fetch('/tickets', {
            method: 'POST',
            body: formData,
            headers: headers,
            credentials: 'same-origin'
        })
            .then(response => {
                if (response.ok) {
                    return response.text();
                } else {
                    console.error('Request failed with status:', response.status);
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
            })
            .then(turboStreamContent => {
                // Appliquer le contenu Turbo Stream manuellement avec gestion des Trusted Types
                if (turboStreamContent && typeof Turbo !== 'undefined') {
                    try {
                        Turbo.renderStreamMessage(turboStreamContent);
                    } catch (error) {
                        console.warn('Turbo renderStreamMessage failed:', error);
                        // Fallback: parser et appliquer manuellement
                        this.applyTurboStreamFallback(turboStreamContent);
                    }
                }

                // Cacher la modale
                if (this.currentModal) {
                    this.currentModal.hide();
                }

                // Réinitialiser
                this.resetSelection();
            })
            .catch(error => {
                console.error('Error creating free ticket:', error);

                // Afficher un message d'erreur
                this.showErrorMessage(`Erreur lors de la création du ticket: ${error.message}`);
            })
            .finally(() => {
                // Réactiver le bouton
                button.disabled = false;
                button.textContent = 'Créer le ticket';
            });
    }

    // Réinitialiser la sélection
    resetSelection() {
        this.selectedTicketType = null;
        this.selectedTicketLabel = null;
        this.selectedTicketPrice = null;
        this.selectedPaymentMethod = null;
        this.selectedQuantity = 1;
    }

    // Réinitialiser la sélection du mode de paiement
    resetPaymentMethodSelection() {
        // Réinitialiser l'état des boutons
        document.querySelectorAll('.payment-method-btn').forEach(btn => {
            btn.classList.remove('btn-success', 'btn-primary');
            if (btn.dataset.paymentMethod === 'cash') {
                btn.classList.add('btn-outline-success');
            } else {
                btn.classList.add('btn-outline-primary');
            }
        });

        // Cacher l'info de sélection
        document.getElementById('paymentSelectedInfo').classList.add('d-none');

        // Désactiver le bouton de confirmation
        document.getElementById('confirmCreateTicket').disabled = true;

        this.selectedPaymentMethod = null;
    }

    // Action appelée quand la modale se ferme
    modalHidden() {
        this.resetSelection();
        this.resetPaymentMethodSelection();

        // Réinitialiser le bouton de confirmation
        const confirmBtn = document.getElementById('confirmCreateTicket');
        if (confirmBtn) {
            confirmBtn.disabled = true;
            confirmBtn.textContent = 'Créer le ticket';
        }
    }

    // Méthode pour afficher un message d'erreur
    showErrorMessage(message) {
        const flashContainer = document.getElementById('flash-messages');
        if (flashContainer) {
            const errorDiv = document.createElement('div');
            errorDiv.className = 'alert alert-danger alert-dismissible fade show';
            errorDiv.role = 'alert';
            errorDiv.innerHTML = `
                <i class="fas fa-exclamation-triangle me-2"></i>
                ${message}
                <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
            `;

            flashContainer.appendChild(errorDiv);
            this.autoHideFlashMessage(errorDiv);
        } else {
            // Fallback si le conteneur flash n'existe pas
            alert(message);
        }
    }

    // Méthode fallback pour appliquer les Turbo Streams manuellement
    applyTurboStreamFallback(content) {
        try {
            // Créer un parser DOM temporaire
            const parser = new DOMParser();
            const doc = parser.parseFromString(content, 'text/html');

            // Rechercher les éléments turbo-stream
            const turboStreams = doc.querySelectorAll('turbo-stream');

            turboStreams.forEach(stream => {
                const action = stream.getAttribute('action');
                const target = stream.getAttribute('target');
                const template = stream.querySelector('template');

                if (template && target) {
                    const targetElement = document.getElementById(target);
                    if (targetElement) {
                        switch (action) {
                            case 'replace':
                                targetElement.outerHTML = template.innerHTML;
                                break;
                            case 'append':
                                targetElement.insertAdjacentHTML('beforeend', template.innerHTML);
                                break;
                            case 'prepend':
                                targetElement.insertAdjacentHTML('afterbegin', template.innerHTML);
                                break;
                            case 'update':
                                targetElement.innerHTML = template.innerHTML;
                                break;
                        }
                    }
                }
            });
        } catch (error) {
            console.error('Fallback Turbo Stream application failed:', error);
            // Si tout échoue, recharger la page
            window.location.reload();
        }
    }

    // Méthodes pour gérer la quantité des tickets payants
    increaseQuantity() {
        const input = document.getElementById('quantityInput');
        const currentValue = parseInt(input.value) || 1;
        const newValue = Math.min(currentValue + 1, 99);
        input.value = newValue;
        this.selectedQuantity = newValue;
        this.updateQuantityDisplay();
    }

    decreaseQuantity() {
        const input = document.getElementById('quantityInput');
        const currentValue = parseInt(input.value) || 1;
        const newValue = Math.max(currentValue - 1, 1);
        input.value = newValue;
        this.selectedQuantity = newValue;
        this.updateQuantityDisplay();
    }

    updateQuantity(event) {
        const value = parseInt(event.target.value) || 1;
        const clampedValue = Math.max(1, Math.min(value, 99));
        event.target.value = clampedValue;
        this.selectedQuantity = clampedValue;
        this.updateQuantityDisplay();
    }

    updateQuantityDisplay() {
        const quantityText = document.getElementById('quantityText');
        const ticketPriceText = document.getElementById('ticketPriceText');

        if (quantityText) {
            quantityText.textContent = this.selectedQuantity === 1 ? '1 ticket' : `${this.selectedQuantity} tickets`;
        }

        // Mettre à jour le prix total
        if (ticketPriceText && this.selectedTicketType) {
            const unitPrice = this.getUnitPrice(this.selectedTicketType);
            const totalPrice = unitPrice * this.selectedQuantity;
            ticketPriceText.textContent = totalPrice === unitPrice ?
                `${unitPrice}€` :
                `${totalPrice}€ (${this.selectedQuantity} × ${unitPrice}€)`;
        }
    }

    // Méthodes pour gérer la quantité des tickets gratuits
    increaseFreeQuantity() {
        const input = document.getElementById('freeQuantityInput');
        const currentValue = parseInt(input.value) || 1;
        const newValue = Math.min(currentValue + 1, 99);
        input.value = newValue;
        this.selectedQuantity = newValue;
        this.updateFreeQuantityDisplay();
    }

    decreaseFreeQuantity() {
        const input = document.getElementById('freeQuantityInput');
        const currentValue = parseInt(input.value) || 1;
        const newValue = Math.max(currentValue - 1, 1);
        input.value = newValue;
        this.selectedQuantity = newValue;
        this.updateFreeQuantityDisplay();
    }

    updateFreeQuantity(event) {
        const value = parseInt(event.target.value) || 1;
        const clampedValue = Math.max(1, Math.min(value, 99));
        event.target.value = clampedValue;
        this.selectedQuantity = clampedValue;
        this.updateFreeQuantityDisplay();
    }

    updateFreeQuantityDisplay() {
        const freeQuantityText = document.getElementById('freeQuantityText');

        if (freeQuantityText) {
            freeQuantityText.textContent = this.selectedQuantity === 1 ? '1 ticket' : `${this.selectedQuantity} tickets`;
        }
    }

    // Méthode utilitaire pour obtenir le prix unitaire
    getUnitPrice(ticketType) {
        switch (ticketType) {
            case 'gratuit': return 0;
            case 't2': return 5;
            case 't10': return 10;
            default: return 0;
        }
    }
}